import { z } from 'zod';
import {
  USER_ROLES,
  ORDER_STATUSES,
  ORDER_PRIORITIES,
  ORDER_SOURCES,
  PROJECT_STATUSES,
  PROGRAM_STATUSES,
  ASSIGNMENT_STATUSES,
  SHIFT_QUARTERS,
  QC_RESULTS,
  QB_SYNC_STATUSES,
  LOCATION_TYPES,
} from '../constants/index';

// =====================================================
// BASE SCHEMAS
// =====================================================

// UUID schema
export const uuidSchema = z.string().uuid();

// Timestamp schema
export const timestampSchema = z.string().datetime();

// =====================================================
// LOCATION SCHEMAS
// =====================================================

export const locationSchema = z.object({
  id: uuidSchema,
  location_name: z.string().min(1, 'Location name is required'),
  location_code: z.string().length(3).nullable(),
  location_type: z.enum([LOCATION_TYPES.CENTER, LOCATION_TYPES.WAREHOUSE, LOCATION_TYPES.OFFICE]).nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().default('SC'),
  zip_code: z.string().nullable(),
  phone: z.string().nullable(),
  timezone: z.string().default('America/New_York'),
  active: z.boolean().default(true),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const locationInsertSchema = locationSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
}).partial({
  location_code: true,
  location_type: true,
  address: true,
  city: true,
  state: true,
  zip_code: true,
  phone: true,
  timezone: true,
  active: true,
});

export type LocationInput = z.infer<typeof locationInsertSchema>;

// =====================================================
// APP USER SCHEMAS
// =====================================================

export const appUserSchema = z.object({
  id: uuidSchema,
  email: z.string().email('Invalid email address'),
  full_name: z.string().min(1, 'Full name is required'),
  role: z.enum([
    USER_ROLES.OPS_MANAGER,
    USER_ROLES.TEAM_LEAD,
    USER_ROLES.PROGRAM_STAFF,
    USER_ROLES.FINANCE,
    USER_ROLES.EXEC,
    USER_ROLES.TRAINEE,
    USER_ROLES.ADMIN,
  ]),
  location_id: uuidSchema.nullable(),
  phone: z.string().nullable(),
  avatar_url: z.string().url().nullable(),
  active: z.boolean().default(true),
  last_login_at: timestampSchema.nullable(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const appUserInsertSchema = appUserSchema.omit({
  created_at: true,
  updated_at: true,
}).partial({
  location_id: true,
  phone: true,
  avatar_url: true,
  active: true,
  last_login_at: true,
});

export type AppUserInput = z.infer<typeof appUserInsertSchema>;

// =====================================================
// CUSTOMER SCHEMAS
// =====================================================

export const customerSchema = z.object({
  id: uuidSchema,
  customer_name: z.string().min(1, 'Customer name is required'),
  contact_name: z.string().nullable(),
  contact_email: z.string().email().nullable(),
  contact_phone: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip_code: z.string().nullable(),
  location_id: uuidSchema.nullable(),
  qb_customer_id: z.string().nullable(),
  qb_sync_status: z.enum([
    QB_SYNC_STATUSES.SYNCED,
    QB_SYNC_STATUSES.PENDING,
    QB_SYNC_STATUSES.ERROR,
    QB_SYNC_STATUSES.NOT_LINKED,
  ]).nullable(),
  qb_last_synced_at: timestampSchema.nullable(),
  payment_terms: z.string().default('Net 30'),
  credit_limit: z.number().positive().nullable(),
  notes: z.string().nullable(),
  tags: z.array(z.string()).nullable(),
  active: z.boolean().default(true),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const customerInsertSchema = customerSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
}).partial({
  contact_name: true,
  contact_email: true,
  contact_phone: true,
  address: true,
  city: true,
  state: true,
  zip_code: true,
  location_id: true,
  qb_customer_id: true,
  qb_sync_status: true,
  qb_last_synced_at: true,
  payment_terms: true,
  credit_limit: true,
  notes: true,
  tags: true,
  active: true,
});

export type CustomerInput = z.infer<typeof customerInsertSchema>;

// =====================================================
// ORDER SCHEMAS
// =====================================================

export const orderSchema = z.object({
  id: uuidSchema,
  order_number: z.string(),
  customer_id: uuidSchema,
  order_date: z.string(), // DATE type
  due_date: z.string().nullable(),
  order_status: z.enum([
    ORDER_STATUSES.NEW,
    ORDER_STATUSES.IN_PROGRESS,
    ORDER_STATUSES.COMPLETED,
    ORDER_STATUSES.CANCELLED,
    ORDER_STATUSES.ON_HOLD,
  ]).default(ORDER_STATUSES.NEW),
  order_description: z.string().nullable(),
  po_number: z.string().nullable(),
  price_override: z.number().positive().nullable(),
  price_override_reason: z.string().nullable(),
  location_id: uuidSchema.nullable(),
  priority: z.enum([
    ORDER_PRIORITIES.LOW,
    ORDER_PRIORITIES.NORMAL,
    ORDER_PRIORITIES.HIGH,
    ORDER_PRIORITIES.URGENT,
  ]).default(ORDER_PRIORITIES.NORMAL),
  source: z.enum([
    ORDER_SOURCES.DIRECT,
    ORDER_SOURCES.WEBSITE,
    ORDER_SOURCES.PHONE,
    ORDER_SOURCES.EMAIL,
    ORDER_SOURCES.REFERRAL,
  ]).nullable(),
  notes: z.string().nullable(),
  created_by: uuidSchema.nullable(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const orderInsertSchema = orderSchema.omit({
  id: true,
  order_number: true, // Auto-generated
  created_at: true,
  updated_at: true,
}).partial({
  order_date: true,
  due_date: true,
  order_status: true,
  order_description: true,
  po_number: true,
  price_override: true,
  price_override_reason: true,
  location_id: true,
  priority: true,
  source: true,
  notes: true,
  created_by: true,
});

export type OrderInput = z.infer<typeof orderInsertSchema>;

// =====================================================
// PROJECT SCHEMAS
// =====================================================

export const projectSchema = z.object({
  id: uuidSchema,
  project_number: z.string(),
  order_id: uuidSchema,
  service_item_id: uuidSchema,
  location_id: uuidSchema.nullable(),
  project_status: z.enum([
    PROJECT_STATUSES.PENDING,
    PROJECT_STATUSES.IN_PROGRESS,
    PROJECT_STATUSES.QC,
    PROJECT_STATUSES.COMPLETED,
    PROJECT_STATUSES.CANCELLED,
    PROJECT_STATUSES.ON_HOLD,
  ]).default(PROJECT_STATUSES.PENDING),
  target_quantity: z.number().int().positive(),
  completed_quantity: z.number().int().min(0).default(0),
  start_date: z.string().nullable(),
  target_completion_date: z.string().nullable(),
  actual_completion_date: z.string().nullable(),
  team_lead_id: uuidSchema.nullable(),
  notes: z.string().nullable(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const projectInsertSchema = projectSchema.omit({
  id: true,
  project_number: true, // Auto-generated
  created_at: true,
  updated_at: true,
}).partial({
  location_id: true,
  project_status: true,
  completed_quantity: true,
  start_date: true,
  target_completion_date: true,
  actual_completion_date: true,
  team_lead_id: true,
  notes: true,
});

export type ProjectInput = z.infer<typeof projectInsertSchema>;

// =====================================================
// TRAINEE SCHEMAS
// =====================================================

export const traineeSchema = z.object({
  id: uuidSchema,
  user_id: uuidSchema,
  location_id: uuidSchema,
  program_start_date: z.string().nullable(),
  program_end_date: z.string().nullable(),
  program_status: z.enum([
    PROGRAM_STATUSES.ACTIVE,
    PROGRAM_STATUSES.GRADUATED,
    PROGRAM_STATUSES.WITHDRAWN,
    PROGRAM_STATUSES.ON_LEAVE,
    PROGRAM_STATUSES.TERMINATED,
  ]).default(PROGRAM_STATUSES.ACTIVE),
  apricot_id: z.string().nullable(),
  emergency_contact_name: z.string().nullable(),
  emergency_contact_phone: z.string().nullable(),
  notes: z.string().nullable(),
  active: z.boolean().default(true),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const traineeInsertSchema = traineeSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
}).partial({
  program_start_date: true,
  program_end_date: true,
  program_status: true,
  apricot_id: true,
  emergency_contact_name: true,
  emergency_contact_phone: true,
  notes: true,
  active: true,
});

export type TraineeInput = z.infer<typeof traineeInsertSchema>;

// =====================================================
// WORK ASSIGNMENT SCHEMAS
// =====================================================

export const workAssignmentSchema = z.object({
  id: uuidSchema,
  project_id: uuidSchema,
  trainee_id: uuidSchema,
  station_id: uuidSchema.nullable(),
  assignment_date: z.string(),
  shift_quarter: z.enum([
    SHIFT_QUARTERS.Q1,
    SHIFT_QUARTERS.Q2,
    SHIFT_QUARTERS.Q3,
    SHIFT_QUARTERS.Q4,
  ]).default(SHIFT_QUARTERS.Q1),
  status: z.enum([
    ASSIGNMENT_STATUSES.SCHEDULED,
    ASSIGNMENT_STATUSES.IN_PROGRESS,
    ASSIGNMENT_STATUSES.COMPLETED,
    ASSIGNMENT_STATUSES.CANCELLED,
    ASSIGNMENT_STATUSES.NO_SHOW,
  ]).default(ASSIGNMENT_STATUSES.SCHEDULED),
  planned_hours: z.number().positive().nullable(),
  actual_hours: z.number().min(0).nullable(),
  notes: z.string().nullable(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const workAssignmentInsertSchema = workAssignmentSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
}).partial({
  station_id: true,
  assignment_date: true,
  shift_quarter: true,
  status: true,
  planned_hours: true,
  actual_hours: true,
  notes: true,
});

export type WorkAssignmentInput = z.infer<typeof workAssignmentInsertSchema>;

// =====================================================
// PRODUCTION LOG SCHEMAS
// =====================================================

export const productionLogSchema = z.object({
  id: uuidSchema,
  work_assignment_id: uuidSchema,
  units_completed: z.number().int().min(0).default(0),
  units_failed: z.number().int().min(0).default(0),
  failure_reason: z.string().nullable(),
  logged_at: timestampSchema,
  logged_by: uuidSchema.nullable(),
  notes: z.string().nullable(),
  created_at: timestampSchema,
});

export const productionLogInsertSchema = productionLogSchema.omit({
  id: true,
  created_at: true,
}).partial({
  units_completed: true,
  units_failed: true,
  failure_reason: true,
  logged_at: true,
  logged_by: true,
  notes: true,
});

export type ProductionLogInput = z.infer<typeof productionLogInsertSchema>;

// =====================================================
// QC CHECK SCHEMAS
// =====================================================

export const qcCheckSchema = z.object({
  id: uuidSchema,
  project_id: uuidSchema,
  checked_by: uuidSchema.nullable(),
  check_date: z.string(),
  sample_size: z.number().int().positive(),
  passed_count: z.number().int().min(0),
  failed_count: z.number().int().min(0).default(0),
  pass_rate: z.number().min(0).max(100).nullable(),
  defect_types: z.array(z.string()).nullable(),
  result: z.enum([
    QC_RESULTS.PASS,
    QC_RESULTS.FAIL,
    QC_RESULTS.CONDITIONAL,
  ]).default(QC_RESULTS.PASS),
  notes: z.string().nullable(),
  created_at: timestampSchema,
});

export const qcCheckInsertSchema = qcCheckSchema.omit({
  id: true,
  created_at: true,
}).partial({
  checked_by: true,
  check_date: true,
  failed_count: true,
  pass_rate: true,
  defect_types: true,
  result: true,
  notes: true,
});

export type QCCheckInput = z.infer<typeof qcCheckInsertSchema>;
